import { useState, useEffect } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useCart } from '../../hooks/useCart';

type Office = 'belgium' | 'uk';

const OFFICES = {
  belgium: {
    id: 'belgium' as Office,
    country: 'Belgium',
    flag: '🇧🇪',
    label: 'Head Office',
    address: 'Leuvenselaan 467, Tienen 3300',
    city: 'Tienen, Belgium',
    phone: '+32 487 261 970',
    email: 'pelpartnercompany@gmail.com',
    hours: 'Mon–Fri: 09:00–18:00 CET',
    mapSrc:
      'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2516.3!2d4.9408!3d50.8027!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47c15e5b5b5b5b5b%3A0x0!2sLeuvenselaan+467%2C+3300+Tienen%2C+Belgium!5e0!3m2!1sen!2sbe!4v1680000000000!5m2!1sen!2sbe',
  },
  uk: {
    id: 'uk' as Office,
    country: 'United Kingdom',
    flag: '🇬🇧',
    label: 'UK Office',
    address: 'London',
    city: 'London, United Kingdom',
    phone: '+32 487 261 970',
    email: 'pelpartnercompany@gmail.com',
    hours: 'Mon–Fri: 09:00–17:00 GMT',
    mapSrc:
      'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2483.0!2d-0.1276!3d51.5074!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47d8a00baf21de75%3A0x52963a5addd52a99!2sLondon%2C+UK!5e0!3m2!1sen!2suk!4v1680000000001!5m2!1sen!2suk',
  },
};

export default function ContactPage() {
  const { getCartCount } = useCart();
  const [activeOffice, setActiveOffice] = useState<Office>('belgium');
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const office = OFFICES[activeOffice];

  useEffect(() => {
    document.title = 'Contact PEL Partner Company | Radiator Specialists Belgium & UK';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Contact PEL Partner Company — Belgium\'s premium radiator specialists. Reach our Belgium office in Tienen or our UK office in London. Fast response within 1 business day.');

    const schemaId = 'schema-contact';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify([
        {
          '@context': 'https://schema.org',
          '@type': 'ContactPage',
          name: 'Contact PEL Partner Company',
          description: 'Contact page for PEL Partner Company — premium radiator specialists in Belgium and the UK.',
          url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: import.meta.env.VITE_SITE_URL || 'https://example.com' },
              { '@type': 'ListItem', position: 2, name: 'Contact', item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact` },
            ],
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          name: 'PEL Partner Company',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com',
          telephone: '+32487261970',
          email: 'pelpartnercompany@gmail.com',
          address: [
            {
              '@type': 'PostalAddress',
              streetAddress: 'Leuvenselaan 467',
              addressLocality: 'Tienen',
              postalCode: '3300',
              addressCountry: 'BE',
            },
            {
              '@type': 'PostalAddress',
              addressLocality: 'London',
              addressCountry: 'GB',
            },
          ],
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 50.8027,
            longitude: 4.9408,
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
              opens: '09:00',
              closes: '18:00',
            },
          ],
          contactPoint: {
            '@type': 'ContactPoint',
            telephone: '+32487261970',
            contactType: 'customer service',
            email: 'pelpartnercompany@gmail.com',
            availableLanguage: ['English', 'Dutch', 'French'],
            areaServed: ['BE', 'GB', 'EU'],
          },
        },
        {
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: 'How long does delivery take?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Approx. 12 working days from order confirmation.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do you offer free shipping?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes — Belgium orders over €3,000 and international orders over €5,000 qualify for free delivery.',
              },
            },
            {
              '@type': 'Question',
              name: 'Can I get a bulk discount?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes! Use our Request a Quote form for volume pricing on bulk radiator orders.',
              },
            },
            {
              '@type': 'Question',
              name: 'What payment methods do you accept?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'We accept Visa, Mastercard, Google Pay, and Bank Transfer.',
              },
            },
            {
              '@type': 'Question',
              name: 'Do you ship outside Belgium?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Yes, we ship premium radiators across Europe and the UK.',
              },
            },
          ],
        },
      ]);
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    const form = e.currentTarget;
    const data = new URLSearchParams();
    const fields = ['name', 'email', 'phone', 'subject', 'message'];
    fields.forEach((f) => {
      const el = form.elements.namedItem(f) as HTMLInputElement | HTMLTextAreaElement | null;
      if (el) data.append(f, el.value);
    });
    try {
      await fetch('https://readdy.ai/api/form/d7a1lc1rcamcd36dbpf0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
    } catch {
      // silent
    } finally {
      setSubmitted(true);
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f9f9f7]">
      <Navbar cartItemCount={getCartCount()} />

      {/* Hero */}
      <div className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ec40b0159b88b3c39dd72bf35cc766b9.jpg"
            alt="PEL Partner Company Office"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/20"></div>
        </div>
        <div className="relative z-10 max-w-[1200px] mx-auto px-6">
          <span className="inline-block bg-white/20 text-white px-4 py-1.5 rounded-full text-xs font-medium uppercase tracking-wider mb-5 backdrop-blur-sm">
            Get in Touch
          </span>
          <h1 className="font-serif text-5xl md:text-6xl font-bold text-white mb-4 leading-tight">
            Contact Us
          </h1>
          <p className="text-white/80 text-base max-w-md leading-relaxed">
            Have a question about our radiators, need a quote, or want to discuss a project? Our team is here to help.
          </p>
          {/* Quick contact pills */}
          <div className="flex flex-wrap gap-3 mt-8">
            <a
              href="tel:+32487261970"
              className="flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-5 py-2.5 rounded-full text-sm hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-phone-line"></i>
              +32 487 261 970
            </a>
            <a
              href="mailto:pelpartnercompany@gmail.com"
              className="flex items-center gap-2 bg-white/15 backdrop-blur-sm text-white px-5 py-2.5 rounded-full text-sm hover:bg-white/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              pelpartnercompany@gmail.com
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-[1200px] mx-auto px-6 py-14">

        {/* Office Selector + Map */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <h2 className="text-xl font-bold text-black">Our Offices</h2>
            <div className="flex bg-white rounded-full p-1 border border-gray-200">
              {(Object.values(OFFICES)).map((o) => (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => setActiveOffice(o.id)}
                  className={`px-5 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    activeOffice === o.id ? 'bg-black text-white' : 'text-gray-600 hover:text-black'
                  }`}
                >
                  {o.flag} {o.country}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Map */}
            <div className="lg:col-span-2 rounded-2xl overflow-hidden h-[380px] bg-gray-100">
              <iframe
                src={office.mapSrc}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title={`${office.country} Office Map`}
              ></iframe>
            </div>

            {/* Office Details */}
            <div className="flex flex-col gap-4">
              <div className="bg-white rounded-2xl p-6 flex-1">
                <div className="flex items-center gap-3 mb-5">
                  <span className="text-3xl">{office.flag}</span>
                  <div>
                    <p className="text-xs text-gray-400 uppercase tracking-wide">{office.label}</p>
                    <h3 className="text-base font-bold text-black">{office.country}</h3>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0 mt-0.5">
                      <i className="ri-map-pin-line text-gray-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Address</p>
                      <p className="text-sm font-medium text-gray-800">{office.address}</p>
                      <p className="text-sm text-gray-600">{office.city}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0">
                      <i className="ri-phone-line text-gray-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Phone</p>
                      <a href={`tel:${office.phone.replace(/\s/g, '')}`} className="text-sm font-medium text-gray-800 hover:text-black cursor-pointer">
                        {office.phone}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0">
                      <i className="ri-mail-line text-gray-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Email</p>
                      <a href={`mailto:${office.email}`} className="text-sm font-medium text-gray-800 hover:text-black cursor-pointer break-all">
                        {office.email}
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-lg flex-shrink-0">
                      <i className="ri-time-line text-gray-600 text-sm"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 mb-0.5">Business Hours</p>
                      <p className="text-sm font-medium text-gray-800">{office.hours}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Closed on weekends &amp; public holidays</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Response time badge */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-2xl p-4 flex items-center gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-green-100 rounded-xl flex-shrink-0">
                  <i className="ri-reply-line text-green-600"></i>
                </div>
                <div>
                  <p className="text-sm font-semibold text-green-900">Fast Response</p>
                  <p className="text-xs text-green-700">We reply within <strong>1 business day</strong></p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Form + Side Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">

          {/* Form */}
          <div className="lg:col-span-2">
            {submitted ? (
              <div className="bg-white rounded-2xl p-12 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-5">
                  <i className="ri-check-line text-3xl text-green-600"></i>
                </div>
                <h3 className="text-2xl font-bold text-black mb-3">Message Sent!</h3>
                <p className="text-gray-600 text-sm mb-2">Thank you for reaching out to PEL Partner Company.</p>
                <p className="text-gray-500 text-sm mb-8">We'll get back to you within <strong>1 business day</strong>.</p>
                <button
                  onClick={() => { setSubmitted(false); setCharCount(0); }}
                  className="px-8 py-3 bg-black text-white rounded-full text-sm font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <div className="bg-white rounded-2xl p-8">
                <h2 className="text-xl font-bold text-black mb-2">Send Us a Message</h2>
                <p className="text-sm text-gray-500 mb-8">Fill in the form below and we'll get back to you as soon as possible.</p>

                <form onSubmit={handleSubmit} data-readdy-form className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Full Name <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        required
                        placeholder="e.g. Thomas Dubois"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        placeholder="+32 ..."
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Email Address <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Subject <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="subject"
                      required
                      placeholder="e.g. Product enquiry, Bulk order, Technical question..."
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa] transition-colors"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                      Message <span className="text-red-400">*</span>
                    </label>
                    <textarea
                      name="message"
                      required
                      rows={6}
                      maxLength={500}
                      placeholder="Tell us how we can help you..."
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-black bg-[#fafafa] resize-none transition-colors"
                    ></textarea>
                    <p className={`text-xs mt-1 text-right ${charCount >= 500 ? 'text-red-500' : 'text-gray-400'}`}>
                      {charCount}/500
                    </p>
                  </div>

                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-black text-white py-4 rounded-full text-sm font-semibold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <i className="ri-loader-4-line animate-spin"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line"></i>
                        Send Message
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-400 text-center">
                    We respect your privacy. Your information is never shared with third parties.
                  </p>
                </form>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1 space-y-5">

            {/* FAQ quick links */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="text-sm font-bold text-black mb-5">Common Questions</h3>
              <div className="space-y-3">
                {[
                  { q: 'How long does delivery take?', a: 'Approx. 12 working days from order confirmation.' },
                  { q: 'Do you offer free shipping?', a: 'Yes — Belgium orders over €3,000 and international orders over €5,000.' },
                  { q: 'Can I get a bulk discount?', a: 'Yes! Use our Request a Quote form for volume pricing.' },
                  { q: 'What payment methods do you accept?', a: 'Visa, Mastercard, Google Pay, and Bank Transfer.' },
                  { q: 'Do you ship outside Belgium?', a: 'Yes, we ship across Europe and the UK.' },
                ].map((item, i) => (
                  <details key={i} className="group border-b border-gray-100 last:border-0 pb-3 last:pb-0">
                    <summary className="text-sm font-medium text-gray-800 cursor-pointer list-none flex items-center justify-between gap-2">
                      {item.q}
                      <i className="ri-add-line text-gray-400 group-open:hidden flex-shrink-0"></i>
                      <i className="ri-subtract-line text-gray-400 hidden group-open:block flex-shrink-0"></i>
                    </summary>
                    <p className="text-xs text-gray-500 mt-2 leading-relaxed">{item.a}</p>
                  </details>
                ))}
              </div>
            </div>

            {/* Social links */}
            <div className="bg-white rounded-2xl p-6">
              <h3 className="text-sm font-bold text-black mb-4">Follow Us</h3>
              <div className="space-y-3">
                {[
                  { icon: 'ri-facebook-fill', label: 'Facebook', handle: '@PELPartnerCompany', href: 'https://facebook.com' },
                  { icon: 'ri-instagram-line', label: 'Instagram', handle: '@pelpartner', href: 'https://instagram.com' },
                  { icon: 'ri-linkedin-box-fill', label: 'LinkedIn', handle: 'PEL Partner Company', href: 'https://linkedin.com' },
                ].map((s) => (
                  <a
                    key={s.label}
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer group"
                  >
                    <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-xl group-hover:bg-black group-hover:text-white transition-colors flex-shrink-0">
                      <i className={`${s.icon} text-gray-600 group-hover:text-white`}></i>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{s.label}</p>
                      <p className="text-xs text-gray-400">{s.handle}</p>
                    </div>
                    <i className="ri-arrow-right-up-line text-gray-300 ml-auto group-hover:text-black transition-colors"></i>
                  </a>
                ))}
              </div>
            </div>

            {/* Quote CTA */}
            <div className="bg-[#0A0A0A] rounded-2xl p-6 text-white">
              <div className="w-10 h-10 flex items-center justify-center bg-white/10 rounded-xl mb-4">
                <i className="ri-file-list-3-line text-white text-lg"></i>
              </div>
              <h3 className="text-sm font-bold mb-2">Need a Bulk Quote?</h3>
              <p className="text-xs text-gray-400 mb-5 leading-relaxed">
                For commercial or large-volume orders, use our dedicated quote form for faster, tailored pricing.
              </p>
              <a
                href="/request-quote"
                className="flex items-center justify-center gap-2 bg-white text-black px-5 py-3 rounded-full text-xs font-semibold hover:bg-gray-100 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line"></i>
                Request a Quote
              </a>
            </div>

          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
